import React, { useEffect, useMemo, useState } from 'react'

import { InformationCircleIcon } from '@heroicons/react/24/outline'
import ReactTooltip from 'react-tooltip'

import {
  PackageOption,
  getAvailableESignPackages,
  getDefaultESignPackage,
  getDocumentLabel,
} from './ESignatureMapping'
import { ESignatureRecipient } from './ESignatureRecipient'
import { Application } from '@/graphql/generated'
import {
  useListApplicationProfilesQuery,
  useListNonApplicantsQuery,
} from '@/services/v2-application/applicationProfile'
import { UserDocument } from '@/types/unlock'

type ESignatureRecipientsProps = {
  primaryApplicantId?: number
  state: any
  dispatch: React.Dispatch<any>
  application: Application
  document: UserDocument | null
}

type FilterAvailableESignPackagesArgs = {
  primaryApplicantId?: number
  state: any
  availableOptions: PackageOption[]
  profileType: string
}

export function filterAvailableESignPackages({
  primaryApplicantId,
  state,
  availableOptions,
  profileType,
}: FilterAvailableESignPackagesArgs) {
  if (profileType === 'non_owning_occupant') {
    const primaryESignatures = state?.eSignatures?.filter(
      (x) => x.applicationProfileId == primaryApplicantId
    )
    if (primaryESignatures?.length == 1) {
      const primaryDoc = primaryESignatures[0]?.doc
      if (['ie', 'ics'].includes(primaryDoc))
        return availableOptions.filter((x) => x.value !== 'non-applicants-authorization')
    }
  }
  return availableOptions
}

export function ESignatureRecipients({
  primaryApplicantId,
  state,
  dispatch,
  application,
  document,
}: ESignatureRecipientsProps) {
  const { data: applicationProfilesData } = useListApplicationProfilesQuery(application?.uuid, {
    skip: !application?.uuid,
  })
  const { data: nonApplicantsData } = useListNonApplicantsQuery(application?.uuid, {
    skip: !application?.uuid,
  })

  const applicationProfiles = applicationProfilesData?.results || []
  const nonApplicants = nonApplicantsData?.results || []

  const sortedApplicants =
    applicationProfiles?.filter(Boolean).sort((a, b) => a?.position - b?.position) || []

  const recipientsOptions = [...sortedApplicants, ...nonApplicants]

  const [checkboxes, setCheckboxes] = useState<boolean[]>(() => recipientsOptions?.map(() => true))
  const [selectedRecipients, setSelectedRecipients] = useState(recipientsOptions)

  const handleCheckboxChange = (idx: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    /** Updates the checkbox state for the specified index. */
    const { checked } = event.target
    const applicantProfile = recipientsOptions[idx]

    setCheckboxes((prev) => {
      const newCheckboxes = [...prev]
      newCheckboxes[idx] = checked
      return newCheckboxes
    })

    // If the checkbox is unchecked, dispatch an action to remove related eSignature
    if (!checked && applicantProfile?.id) {
      dispatch({
        type: 'setESignatures',
        payload: {
          action: 'remove',
          applicationProfileId: applicantProfile.id,
        },
      })
    }
  }

  useEffect(() => {
    /** Dispatches the selected applicants when checkbox state changes. */
    const recipients = recipientsOptions
      .filter((_, idx) => checkboxes[idx])
      .map((applicant) => applicant)

    setSelectedRecipients(recipients)

    dispatch({
      type: 'setRecipients',
      payload: { recipients },
    })
  }, [checkboxes])

  return (
    <>
      <div className='flex mb-8 items-end justify-end flex-row'>
        <span>
          <InformationCircleIcon
            className='w-5'
            data-tip='Generate New'
            data-for='generate-new'
          />
          <ReactTooltip
            place='left'
            id='generate-new'
          >
            Generate a new document if non-exist or a newer version is required
          </ReactTooltip>
        </span>
      </div>
      <div>
        {recipientsOptions?.map((applicant, idx) => {
          const documentName = getDocumentLabel(document)
          const defaultPackageValue = getDefaultESignPackage(documentName, applicant.profile_type)
          const availableOptions = filterAvailableESignPackages({
            primaryApplicantId,
            state,
            profileType: applicant.profile_type,
            availableOptions: getAvailableESignPackages(documentName, applicant.profile_type),
          })
          return (
            <ESignatureRecipient
              key={applicant?.id}
              applicant={applicant}
              availableOptions={availableOptions}
              checkboxes={checkboxes}
              defaultPackageValue={defaultPackageValue}
              dispatch={dispatch}
              handleCheckboxChange={handleCheckboxChange}
              idx={idx}
              recipientsOptions={recipientsOptions}
              selectedRecipients={selectedRecipients}
            />
          )
        })}
      </div>
    </>
  )
}
